import type {
  CsatActivePrompt,
  CsatCampaign,
  CsatResourceType,
  CsatSubmission,
  CsatTopic
} from '@/types/csat'

import { promptKey } from './store'

// Recurso (type, id) elegivel para interaction quando a resource_config da
// campanha habilita o resource_kind e o id casa com resource_ids (vazio =
// todos). enabled: false vence ids preenchidos.
const resourceEligible = (
  campaign: CsatCampaign,
  type: CsatResourceType,
  id: number
): boolean => {
  const entry = campaign.resource_config?.[type]
  if (!entry || entry.enabled !== true) return false
  const ids = entry.resource_ids ?? []
  return ids.length === 0 || ids.includes(id)
}

export const isWithinWindow = (
  startsAt: string | null,
  endsAt: string | null,
  now: number = Date.now()
) => {
  if (startsAt && now < new Date(startsAt).getTime()) return false
  if (endsAt && now > new Date(endsAt).getTime()) return false
  return true
}

const interactionAnswered = (
  submissions: CsatSubmission[],
  campaignId: number,
  type: string,
  id: number
) =>
  submissions.some(
    s =>
      s.csat_campaign_id === campaignId &&
      s.context_type === type &&
      s.context_id === id
  )

const topicAnswered = (submissions: CsatSubmission[], topicId: number) =>
  submissions.some(s => s.csat_topic_id === topicId)

export interface CompletionContext {
  campaigns: CsatCampaign[]
  submissions: CsatSubmission[]
  type: CsatResourceType
  id: number
  isDismissed: (key: string) => boolean
  // Seletor injetável de aleatório, para testes determinísticos.
  pickRandom?: <T>(items: T[]) => T
  now?: number
}

// Decide interaction OU topic para um recurso recém-concluído.
// Prioridade: interaction compatível > topic aleatório elegível (D5).
export const decideForCompletion = (
  ctx: CompletionContext
): CsatActivePrompt | null => {
  const {
    campaigns,
    submissions,
    type,
    id,
    isDismissed,
    pickRandom = items => items[Math.floor(Math.random() * items.length)],
    now = Date.now()
  } = ctx

  // 1. interaction compatível com o recurso concluído (via resource_config)
  const interaction = campaigns.find(c => c.kind === 'interaction' && c.active)

  if (interaction && resourceEligible(interaction, type, id)) {
    const key = promptKey.interaction(interaction.id, type, id)
    const answered = interactionAnswered(submissions, interaction.id, type, id)
    const dismissed = isDismissed(key)

    // Level permite repetição — re-exibe a pesquisa de interação ao refazer,
    // ignorando dismiss e submissions anteriores.
    if (type === 'Level') {
      return { campaign: interaction, contextType: type, contextId: id }
    }

    if (!dismissed && !answered) {
      return { campaign: interaction, contextType: type, contextId: id }
    }
    // Demais recursos bloqueiam fallback quando cobertos pela resource_config.
    return null
  }

  // 2. topic aleatório elegível como fallback
  const eligibleTopics: { campaign: CsatCampaign; topic: CsatTopic }[] = []
  campaigns
    .filter(c => c.kind === 'topic' && c.active)
    .filter(c => isWithinWindow(c.starts_at, c.ends_at, now))
    .forEach(campaign => {
      ;(campaign.topics ?? [])
        .filter(t => t.active)
        .filter(t => !topicAnswered(submissions, t.id))
        .filter(t => !isDismissed(promptKey.topic(t.id)))
        .forEach(topic => eligibleTopics.push({ campaign, topic }))
    })

  if (eligibleTopics.length > 0) {
    const pick = pickRandom(eligibleTopics)
    return {
      campaign: pick.campaign,
      topic: pick.topic,
      contextType: type,
      contextId: id
    }
  }

  return null
}

export interface FinalContext {
  campaigns: CsatCampaign[]
  submissions: CsatSubmission[]
  today: string
  isDismissed: (key: string) => boolean
  now?: number
}

// app_open: decide campanha final com momento ativo e dentro do limite diário.
export const decideFinal = (ctx: FinalContext): CsatActivePrompt | null => {
  const { campaigns, submissions, today, isDismissed, now = Date.now() } = ctx

  const final = campaigns.find(c => c.kind === 'final' && c.active)
  if (!final) return null

  const hasActiveMoment = (final.collection_moments ?? []).some(
    m => m.active && isWithinWindow(m.starts_at, m.ends_at, now)
  )
  if (!hasActiveMoment) return null

  const answeredToday = submissions.filter(
    s =>
      s.csat_campaign_id === final.id &&
      s.context_type == null &&
      s.csat_topic_id == null &&
      s.answered_on === today
  ).length
  const limit = final.max_answers_per_day ?? 1
  if (answeredToday >= limit) return null

  if (isDismissed(promptKey.final(final.id, today))) return null

  return { campaign: final }
}
